import { useTranslation } from "react-i18next";
import zoullooCover from "../../../assets/images/zoulloo_cover.jpg";

import {
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	Container,
	Skeleton,
	Typography
} from "@mui/material";
import { useState } from "react";
import { useFreelancerContext } from "../../../contexts/useFreelancerContext";
import { usePatchMyProfileFreelancerOnboardingStep } from "../../../hooks/useFreelancerProfile";
import { FreelancerOnboardingFormStep } from "../../../models/enums/FreelancerOnboardingFormStep";

const FreelancerOnboardingMyProfile: React.FC = () => {
	const {
		setCurrentOnboardingFormStep,
		freelancerQueryResult,
		freelancerProfileQueryResult
	} = useFreelancerContext();
	const { data: freelancer } = freelancerQueryResult;
	const { data: freelancerProfile, isLoading: isFreelancerProfileLoading } =
		freelancerProfileQueryResult;
	const patchFreelancerOnboarding = usePatchMyProfileFreelancerOnboardingStep({
		hideAllSnackbars: true
	});
	const { t } = useTranslation();
	const [isSavingPending, setIsSavingPending] = useState(false);

	// Define the steps data structure for easier mapping
	const applicationSteps = [
		{
			key: "step1",
			titleKey: "steps.step1.title",
			descriptionKey: "steps.step1.description"
		},
		{
			key: "step2",
			titleKey: "steps.step2.title",
			descriptionKey: "steps.step2.description"
		},
		{
			key: "step3",
			titleKey: "steps.step3.title",
			descriptionKey: "steps.step3.description"
		},
		{
			key: "step4",
			titleKey: "steps.step4.title",
			descriptionKey: "steps.step4.description"
		},
		{
			key: "step5",
			titleKey: "steps.step5.title",
			descriptionKey: "steps.step5.description"
		}
	];

	if (isFreelancerProfileLoading || !freelancerProfile) {
		return (
			<Card
				sx={{
					marginX: "32px",
					marginY: "48px"
				}}
			>
				<CardContent sx={{ userSelect: "text" }}>
					<Container>
						<Typography variant="h4" component="h1" color="textPrimary" gutterBottom>
							<Skeleton variant="text" width="60%" />
						</Typography>
						<Typography variant="h4" component="h1" color="textPrimary" gutterBottom>
							<Skeleton variant="text" width="60%" />
						</Typography>
						<Typography variant="h4" component="h1" color="textPrimary" gutterBottom>
							<Skeleton variant="text" width="60%" />
						</Typography>
						<Typography variant="h4" component="h1" color="textPrimary" gutterBottom>
							<Skeleton variant="text" width="60%" />
						</Typography>
						<Typography
							variant="body1"
							color="textSecondary"
							lineHeight={2.2}
							gutterBottom
						>
							<Skeleton variant="text" width="80%" />
						</Typography>
						<Typography variant="body1" color="textSecondary" gutterBottom>
							<Skeleton variant="text" width="90%" />
						</Typography>
						<Typography
							variant="body1"
							color="textSecondary"
							lineHeight={2.2}
							gutterBottom
						>
							<Skeleton variant="text" width="70%" />
						</Typography>

						<Box textAlign="left">
							{[...Array(5)].map((_, index) => (
								<Typography
									key={index}
									variant="body1"
									color="textSecondary"
									gutterBottom
								>
									<Skeleton variant="text" width="85%" />
								</Typography>
							))}
						</Box>
						<Skeleton variant="text" width={"353px"} height={"80px"} />
					</Container>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card
			sx={{
				marginX: "32px",
				marginY: "48px"
			}}
		>
			<CardMedia
				component="img"
				image={zoullooCover}
				alt={t("freelancerOnboardingMyProfile.imageAlt")}
				height={"120px"}
			/>
			<CardContent sx={{ userSelect: "text", paddingTop: "35px" }}>
				<Container sx={{ margin: 0, maxWidth: "100% !important" }}>
					<Typography variant="h4" component="h1" color="textPrimary" gutterBottom>
						{t("freelancerOnboardingMyProfile.welcome")} {freelancer?.user.firstName} 👋
					</Typography>
					<Typography variant="body1" color="textSecondary" lineHeight={2.2} gutterBottom>
						{t("freelancerOnboardingMyProfile.thankYou")}
					</Typography>
					<Typography variant="body1" color="textSecondary" gutterBottom>
						{t("freelancerOnboardingMyProfile.introduction")}
					</Typography>
					<Typography variant="body1" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
						{t("freelancerOnboardingMyProfile.methodology")}
					</Typography>
					<Typography
						variant="body1"
						color="textSecondary"
						lineHeight={2.2}
						gutterBottom
						sx={{ mt: 2 }}
					>
						{t("freelancerOnboardingMyProfile.applicationSteps")}
					</Typography>

					<Box textAlign="left">
						{applicationSteps.map((step, index) => (
							<Typography
								key={step.key}
								variant="body1"
								color="textSecondary"
								gutterBottom
								lineHeight={1.8}
							>
								<Typography component={"span"} fontWeight={"bold"}>
									{index + 1}. {t(`freelancerOnboardingMyProfile.${step.titleKey}`)} :
								</Typography>{" "}
								{t(`freelancerOnboardingMyProfile.${step.descriptionKey}`)}
							</Typography>
						))}
					</Box>

					<Typography
						variant="body1"
						color="textSecondary"
						sx={{ mt: 3 }}
						dangerouslySetInnerHTML={{ __html: t("freelancerOnboardingMyProfile.ready") }}
					/>

					<Button
						variant="contained"
						color="secondary"
						sx={{ mt: 3 }}
						disabled={isSavingPending}
						onClick={async () => {
							try {
								setIsSavingPending(true);

								await patchFreelancerOnboarding.mutateAsync({
									freelancerProfile,
									newStep: FreelancerOnboardingFormStep.MyExpertise
								});

								setIsSavingPending(false);
								setCurrentOnboardingFormStep(FreelancerOnboardingFormStep.MyExpertise);
							} catch (error) {
								console.error("Error while saving:", error);
								setIsSavingPending(false);
							}
						}}
					>
						{t("freelancerOnboardingMyProfile.startButton")}
					</Button>
				</Container>
			</CardContent>
		</Card>
	);
};

export default FreelancerOnboardingMyProfile;
